import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { usePopper } from 'react-popper';
import FocusTrap from 'focus-trap-react';

import useOutsideClick from '../utils/hooks/useOutsideClick';

import PopoverHeader, { PopoverHeaderProps } from './PopoverHeader';
import PopoverBody, { PopoverBodyProps } from './PopoverBody';
import PopoverFooter, { PopoverFooterProps } from './PopoverFooter';
import Portal from '../Portal';

import styles from './Popover.module.scss';

export interface PopoverComposition {
  Header: React.FC<PopoverHeaderProps>;
  Body: React.FC<PopoverBodyProps>;
  Footer: React.FC<PopoverFooterProps>;
}
export interface Props {
  /** Children of Popover which should have one Popover.Header, Popover.Body, and Popover.Footer. */
  children?: [
    React.ReactElement<PopoverHeaderProps>,
    React.ReactElement<PopoverBodyProps>,
    React.ReactElement<PopoverFooterProps>,
  ];
  /** CSS className for Popover container div. */
  className?: Parameters<typeof cx>[0];
  /** When true, the Popover will be visible. */
  isOpen: boolean;
  /** Accessible name for the Popover. */
  label: string;
  /** Function that will be called when the Popover is closing. */
  onClose?: (event?: MouseEvent) => void;
  /** Function that will be called when the the user clicks outside while the Popover is open. */
  onOutsideClick?: () => void;
  /** Setter for toggling isOpen. */
  setOpen: (state: boolean) => void;
  /** Reference to the target element. */
  targetElement: HTMLElement | null;
}

// This resolves `ReferenceError: window is not defined` when using in NextJS.
const isBrowser = () => typeof window !== 'undefined';

export const Popover: React.FC<Props> & PopoverComposition = ({
  children,
  className,
  isOpen = false,
  targetElement,
  label,
  setOpen,
  onClose,
  onOutsideClick,
}) => {
  const [popoverElement, setPopoverElement] = useState<HTMLElement | null>(
    null,
  );
  const [currentOffset, setCurrentOffset] = useState<[number, number]>([0, 4]);

  // Close the popover element when user clicks outside of popover
  useOutsideClick(popoverElement, () => {
    setOpen(false);
    if (onOutsideClick) {
      onOutsideClick();
    }
  });

  // Closes dialog when escape key is pressed.
  useEffect(() => {
    const handleUserKeyPress = (e: any) => {
      if (isOpen && e.key === 'Escape') {
        setOpen(false);
      }
    };

    // IsBrowser is checking if the code is running in the server or client which is primary for NextJS.
    isBrowser() && window.addEventListener('keydown', handleUserKeyPress);

    return () => {
      isBrowser() && window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [isOpen, setOpen]);

  const popper = usePopper(targetElement, popoverElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          // https://popper.js.org/docs/v2/modifiers/offset/
          offset: currentOffset,
        },
      },
    ],
  });

  useEffect(() => {
    // Calculating the popover x offset so it left aligns with target element.
    if (popoverElement !== null && targetElement !== null) {
      const { width: targetWidth } = targetElement?.getBoundingClientRect();
      const { width: popoverWidth } = popoverElement?.getBoundingClientRect();

      let popoverXOffset = (popoverWidth - targetWidth) / 2;

      setCurrentOffset([popoverXOffset, 4]);
    }
  }, [targetElement, popoverElement]);

  useEffect(() => {
    // Invokes onClose function when popover unmounts
    return () => {
      if (popoverElement !== null && onClose !== undefined && isOpen === true) {
        onClose();
      }
    };
  }, [isOpen, onClose, setOpen, popoverElement]);

  return (
    <>
      {isOpen && (
        <Portal className='portal-popover'>
          <FocusTrap>
            <div
              role='dialog'
              {...popper.attributes.popper}
              style={popper.styles.popper}
              className={cx(styles.popover, className)}
              ref={setPopoverElement}
              arial-label={label}
              arial-hidden={!isOpen ? true : undefined}
            >
              {children}
            </div>
          </FocusTrap>
        </Portal>
      )}
    </>
  );
};

Popover.displayName = 'Popover';

Popover.Header = PopoverHeader;
Popover.Body = PopoverBody;
Popover.Footer = PopoverFooter;

export default Popover;
export { styles };
